import { FC } from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import { styled } from '@mui/material/styles';
import { Control, Controller, UseFormGetValues } from 'react-hook-form';
import { Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import StaticField from 'further-ui/components/StaticField';
import BlockNoteEditor from 'further-ui/components/BlockNoteEditor';
import { pluralize } from 'further-ui/utils';
import { useNotification } from 'hooks/ui/useNotification';
import { BulletinUpdateFormValues } from '../schema';
import Attachments from './Attachments';
import AttachmentsUpload from './AttachmentsUpload';
import AttachmentSettings from './AttachmentSettings';
import UkFinancialPromotionNotice from './UkFinancialPromotionNotice';

const OuterColumn = styled(Column)({
  maxWidth: '800px',
});

type Props = {
  control: Control<BulletinUpdateFormValues>;
  getValues: UseFormGetValues<BulletinUpdateFormValues>;
  readOnly: boolean;
  hasUkInvestorWithCommitment: boolean;
};

const UpdateContent: FC<Props> = ({
  control,
  getValues,
  readOnly,
  hasUkInvestorWithCommitment,
}) => {
  const { error } = useNotification();

  const subject = getValues('subject');
  const title = getValues('title');
  const commentary = getValues('commentary');
  const attachments = getValues('attachments');

  return readOnly ? (
    <Column spacing="sm">
      <Typography variant="h6">Custom update content</Typography>
      <Column spacing="lg">
        <StaticField label="Subject line">{subject}</StaticField>
        <StaticField label="Body title">{title}</StaticField>
        <StaticField label="Body text">
          <BlockNoteEditor value={commentary} readOnly />
        </StaticField>
        <StaticField
          label={`Attached ${pluralize('file', attachments.length)}`}
        >
          <Attachments attachments={attachments} />
        </StaticField>
      </Column>
    </Column>
  ) : (
    <>
      <Controller
        control={control}
        name="subject"
        render={({ field, fieldState }) => (
          <FormField label="Subject line" spacing="sm" size="md">
            <TextField
              fullWidth
              size="small"
              placeholder="Enter subject line"
              value={field.value}
              onChange={field.onChange}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          </FormField>
        )}
      />
      <Controller
        control={control}
        name="title"
        render={({ field, fieldState }) => (
          <FormField label="Body title" spacing="sm" size="md">
            <TextField
              fullWidth
              size="small"
              placeholder="Enter body title"
              value={field.value}
              onChange={field.onChange}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          </FormField>
        )}
      />
      <OuterColumn spacing="sm">
        <Typography variant="h6">Body text (including sign-off)</Typography>
        <Controller
          control={control}
          name="commentary"
          render={({ field, fieldState }) => (
            <Column spacing="xs">
              <BlockNoteEditor
                value={field.value}
                extraLines={1}
                showTableInToolbar={false}
                onChange={field.onChange}
                onError={error}
                placeholder="Dear {{recipient name}}, enter body text..."
              />
              {fieldState.error ? (
                <FormHelperText error>
                  {fieldState.error.message}
                </FormHelperText>
              ) : null}
            </Column>
          )}
        />
      </OuterColumn>
      <OuterColumn spacing="sm">
        <AttachmentsUpload control={control} />
      </OuterColumn>
      <OuterColumn spacing="sm">
        <AttachmentSettings control={control} />
      </OuterColumn>
      {hasUkInvestorWithCommitment ? (
        <>
          <Divider />
          <OuterColumn>
            <UkFinancialPromotionNotice control={control} />
          </OuterColumn>
        </>
      ) : null}
    </>
  );
};

export default UpdateContent;
